import React, { useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Chart } from 'react-chartjs-2';
import { chartColors } from '@/utils/chartColors';
import { useRsColors } from '@/hooks/useRsColors';
import { useTheme } from '@/contexts/ThemeContext';
import { getSmartYAxisConfig, parseDateLiteral, inventoryChangeAxisLabel, isPastThreeYearMark, isWithinThreeYearRange } from '@/utils/chartAxisUtils';
import { getChartFontSize } from '@/utils/chartTypography';
import useChartWindow from '@/hooks/useChartWindow';
import ChartRangeSlider from '@/components/charts/ChartRangeSlider';
import { getDefaultPreviousYearWindowFromLabels } from '@/utils/chartWindow';
import { CheckboxDropdown } from '@/components/common/CheckboxDropdown';
import { createVerticalHoverLinePlugin } from '@/utils/chartPlugins';
import FreeTrialFade from '@/components/common/FreeTrialFade';

ChartJS.register(
  CategoryScale, LinearScale, BarElement, PointElement, LineElement, Title, Tooltip, Legend, ChartDataLabels
);

interface DemandProductionCapacityChartProps {
  production: (number | null)[];
  exportsTotal?: (number | null)[];
  demand: (number | null)[];
  capacity: (number | null)[];
  labels: string[];
  polymer?: string;
  className?: string;
  showBorder?: boolean;
  showDataLabels?: boolean;
  selectedResinType?: 'pe' | 'hdpe' | 'ldpe' | 'lldpe';
  onResinTypeChange?: (type: 'pe' | 'hdpe' | 'ldpe' | 'lldpe') => void;
  isOverviewPage?: boolean;
}

const DemandProductionCapacityChart: React.FC<DemandProductionCapacityChartProps> = ({
  production,
  exportsTotal,
  demand,
  capacity,
  labels,
  polymer,
  className,
  showBorder = false,
  showDataLabels = true,
  selectedResinType,
  onResinTypeChange,
  isOverviewPage = false,
}) => {
  const rs = useRsColors();
  const { isDark } = useTheme();
  const formatCompactValue = (value: number) => {
    const abs = Math.abs(value);
    if (abs >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
    if (abs >= 1_000) return `${(value / 1_000).toFixed(1)}k`;
    return Math.round(value).toString();
  };
  const [visibleSeries, setVisibleSeries] = React.useState({
    production: true,
    demand: true,
    capacity: true,
    exportsTotal: true,
  });
  const [isSmallButtonLayout, setIsSmallButtonLayout] = React.useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 749px)').matches,
  );
  const [isTinyButtonLayout, setIsTinyButtonLayout] = React.useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 550px)').matches,
  );
  const [isSmallCenterButtons, setIsSmallCenterButtons] = React.useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 750px)').matches,
  );
  const [isSmallResinSelector, setIsSmallResinSelector] = React.useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 750px)').matches,
  );
  const [isLargeResinSelector, setIsLargeResinSelector] = React.useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1024px) and (max-width: 1104px)').matches,
  );
  const [isXLargeResinSelector, setIsXLargeResinSelector] = React.useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 1105px)').matches,
  );
  const [isPVCSeriesNavigation, setIsPVCSeriesNavigation] = React.useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 750px) and (max-width: 1140px)').matches,
  );
  const [seriesNavigationIndex, setSeriesNavigationIndex] = React.useState(0);

  React.useEffect(() => {
    const mq = window.matchMedia('(max-width: 749px)');
    const tinyMq = window.matchMedia('(max-width: 550px)');
    const centerMq = window.matchMedia('(max-width: 750px)');
    const smallResinMq = window.matchMedia('(max-width: 750px)');
    const largeResinMq = window.matchMedia('(min-width: 1024px) and (max-width: 1104px)');
    const xlargeResinMq = window.matchMedia('(min-width: 1105px)');
    const pvcSeriesMq = window.matchMedia('(min-width: 750px) and (max-width: 1140px)');
    const handler = (e: MediaQueryListEvent) => setIsSmallButtonLayout(e.matches);
    const tinyHandler = (e: MediaQueryListEvent) => setIsTinyButtonLayout(e.matches);
    const centerHandler = (e: MediaQueryListEvent) => setIsSmallCenterButtons(e.matches);
    const smallResinHandler = (e: MediaQueryListEvent) => setIsSmallResinSelector(e.matches);
    const largeResinHandler = (e: MediaQueryListEvent) => setIsLargeResinSelector(e.matches);
    const xlargeResinHandler = (e: MediaQueryListEvent) => setIsXLargeResinSelector(e.matches);
    const pvcSeriesHandler = (e: MediaQueryListEvent) => setIsPVCSeriesNavigation(e.matches);
    mq.addEventListener('change', handler);
    tinyMq.addEventListener('change', tinyHandler);
    centerMq.addEventListener('change', centerHandler);
    smallResinMq.addEventListener('change', smallResinHandler);
    largeResinMq.addEventListener('change', largeResinHandler);
    xlargeResinMq.addEventListener('change', xlargeResinHandler);
    pvcSeriesMq.addEventListener('change', pvcSeriesHandler);
    return () => {
      mq.removeEventListener('change', handler);
      tinyMq.removeEventListener('change', tinyHandler);
      centerMq.removeEventListener('change', centerHandler);
      smallResinMq.removeEventListener('change', smallResinHandler);
      largeResinMq.removeEventListener('change', largeResinHandler);
      xlargeResinMq.removeEventListener('change', xlargeResinHandler);
      pvcSeriesMq.removeEventListener('change', pvcSeriesHandler);
    };
  }, []);

  const productionColor = useMemo(() => {
    return chartColors.withOpacity(chartColors.primary, chartColors.opacity.heavy);
  }, []);

  const exportsColor = useMemo(() => {
    if (polymer === 'PVC') {
      return chartColors.withOpacity(chartColors.secondary, chartColors.opacity.heavy);
    }
    return chartColors.withOpacity(chartColors.success, chartColors.opacity.heavy);
  }, [polymer]);

  const demandColor = useMemo(() => {
    if (polymer === 'PVC') {
      return chartColors.withOpacity(chartColors.primary, chartColors.opacity.heavy);
    }
    return chartColors.withOpacity(chartColors.secondary, chartColors.opacity.heavy);
  }, [polymer]);

  const defaultDateWindow = useMemo(
    () => getDefaultPreviousYearWindowFromLabels(labels),
    [labels],
  );

  const { startIndex, endIndex, setWindow, hasWindowing } = useChartWindow(labels.length, {
    defaultStartIndex: defaultDateWindow.startIndex,
    defaultEndIndex: defaultDateWindow.endIndex,
  });

  const visibleLabels = useMemo(
    () => labels.slice(startIndex, endIndex + 1),
    [labels, startIndex, endIndex],
  );

  const visibleProduction = useMemo(
    () => production.slice(startIndex, endIndex + 1),
    [production, startIndex, endIndex],
  );
  const visibleExportsTotal = useMemo(
    () => exportsTotal?.slice(startIndex, endIndex + 1),
    [exportsTotal, startIndex, endIndex],
  );
  const visibleDemand = useMemo(
    () => demand.slice(startIndex, endIndex + 1),
    [demand, startIndex, endIndex],
  );
  const visibleCapacity = useMemo(
    () => capacity.slice(startIndex, endIndex + 1),
    [capacity, startIndex, endIndex],
  );



  const chartData: any = {
    labels: visibleLabels,
    datasets: [
      ...(visibleSeries.production
        ? [
            {
              type: polymer === 'PVC' ? 'line' as const : 'bar' as const,
              label: 'Production',
              data: visibleProduction,
              borderColor: productionColor,
              backgroundColor: productionColor,
              pointRadius: 3,
              borderWidth: 2,
              borderRadius: 2,
              tension: 0.3,
              yAxisID: 'y',
            },
          ]
        : []),

      ...(visibleSeries.demand
        ? [
            {
              type: 'bar' as const,
              label: 'Demand / Sales',
              data: visibleDemand,
              backgroundColor: demandColor,
              borderRadius: 2,
              yAxisID: 'y',
            },
          ]
        : []),

      ...(polymer === 'PVC' && exportsTotal && visibleSeries.exportsTotal
        ? [
            {
              type: 'bar' as const,
              label: 'Exports Total',
              data: visibleExportsTotal,
              backgroundColor: exportsColor,
              borderRadius: 2,
              yAxisID: 'y',
            },
          ]
        : []),

      ...(visibleSeries.capacity
        ? [
            {
              type: 'line' as const,
              label: 'Capacity',
              data: visibleCapacity,
              borderColor: chartColors.warning,
              backgroundColor: 'transparent',
              pointRadius: 3,
              borderWidth: 2,
              tension: 0.3,
              yAxisID: 'y',
            },
          ]
        : []),
    ],
  };

  const options: any = {
    responsive: true,
    maintainAspectRatio: false,
    layout: {
      padding: { top: 20, right: 12, bottom: 8, left: 8 },
    },
    plugins: {
      legend: {
        display: false,
        position: 'top' as const,
        labels: {
          color: rs.offWhite || '#f3f4f6',
          usePointStyle: true,
          font: { size: getChartFontSize() },
        },
      },
      tooltip: {
        backgroundColor: rs.offBlack || '#1a1a1a',
        titleColor: rs.offWhite || '#f3f4f6',
        bodyColor: rs.offWhite || '#f3f4f6',
        borderColor: rs.jetLight || '#3d3d3d',
        borderWidth: 1,
        mode: 'index',
        intersect: false,
        callbacks: {
          label: (ctx: any) => {
            const value = Number(ctx.raw);
            if (!Number.isFinite(value)) return `${ctx.dataset.label}: -`;
            return `${ctx.dataset.label}: ${formatCompactValue(value)}`;
          },
        },
      },
      datalabels: {
        // Show labels whenever the visible window spans ≤ 3 years (anywhere on the timeline);
        // off entirely when showDataLabels is false (e.g. overview pages)
        display: showDataLabels && isWithinThreeYearRange(visibleLabels),
        color: (ctx: any) => {
          const isDemandDataset = ctx.dataset.label?.includes('Demand');
          const isProductionDataset = ctx.dataset.label?.includes('Production');
          const isExportsDataset = ctx.dataset.label?.includes('Exports');

          if (polymer === 'PVC') {
            if (isDark) {
              // Dark mode: production white, demand/sales black
              if (isProductionDataset) return '#ffffff';
              if (isDemandDataset) return '#000000';
            } else {
              // Light mode: exports total white
              if (isExportsDataset) return '#ffffff';
            }
          } else {
            // PE, PP, PS
            if (isDark && isProductionDataset) return '#000000';
            if (!isDark && isDemandDataset) return '#ffffff';
          }

          return rs.offWhite || '#f3f4f6';
        },
        font: { size: 10, weight: 'bold' },
        anchor: (ctx: any) => ctx.dataset.type === 'line' ? 'end' : 'center',
        align: (ctx: any) => ctx.dataset.type === 'line' ? 'top' : 'center',
        offset: 0,
        padding: 4,
        clamp: true,
        clip: false,
        formatter: (value: number | null) => {
          if (value === null || value === undefined) return '';
          return formatCompactValue(Number(value));
        },
      },
    },
    scales: {
      x: {
        ticks: {
          color: rs.offWhite || '#f3f4f6',
          font: { size: getChartFontSize() },
          maxRotation: 0,
          minRotation: 0,
          autoSkip: false,
          callback: (_value: any, index: number) => {
            const lbl = visibleLabels[index];
            const d = parseDateLiteral(String(lbl));
            // <3yr window: every 6 months (MMM yyyy); dragged past 3yr: year only
            if (!isNaN(d.getTime())) {
              return inventoryChangeAxisLabel(d, isPastThreeYearMark(visibleLabels)) || null;
            }
            return lbl;
          },
        },
        grid: { color: rs.gridLine },
      },
      y: {
        ticks: {
          color: rs.offWhite || '#f3f4f6',
          font: { size: getChartFontSize() },
          callback: (value: any) => {
            const numeric = Number(value);
            if (!Number.isFinite(numeric)) return '';
            return formatCompactValue(numeric);
          },
        },
        grid: { color: rs.gridLine },
        title: {
          display: true,
          text: 'Thousand',
          color: rs.offWhite || '#f3f4f6',
          font: { size: getChartFontSize() },
        },
        ...getSmartYAxisConfig(
          [...visibleProduction, ...visibleDemand, ...visibleCapacity].filter((v): v is number => v != null)
        ),
        ...(polymer === 'PVC' ? { min: 10000 } : {}),
      },
    },
  };

  const hoverLinePlugin = useMemo(() => createVerticalHoverLinePlugin(rs), [rs, isDark]);

  const toggleSeries = (key: 'production' | 'demand' | 'capacity' | 'exportsTotal') => {
    setVisibleSeries((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const adjustedClassName = isOverviewPage && isLargeResinSelector && className
    ? className.replace('h-[400px]', 'h-[455px]').replace('h-[575px]', 'h-[625px]')
    : className;

  return (
    <div
      className={adjustedClassName || 'w-full h-full flex flex-col'}
      data-testid="demand-production-capacity-chart"
    >
      {/* BUTTONS OUTSIDE CHART */}
      {selectedResinType && onResinTypeChange && (isSmallResinSelector || (polymer === 'PE' && isLargeResinSelector)) && (
        <div className="flex justify-center mb-3">
          <CheckboxDropdown
            label="PE Type"
            options={['ALL PE', 'HDPE', 'LDPE', 'LLDPE']}
            selected={new Set([selectedResinType === 'pe' ? 'ALL PE' : selectedResinType.toUpperCase()])}
            onChange={(selected) => {
              const value = Array.from(selected)[0];
              const type = value === 'ALL PE' ? 'pe' : value.toLowerCase() as 'pe' | 'hdpe' | 'ldpe' | 'lldpe';
              onResinTypeChange(type);
            }}
            singleSelect={true}
            hideCount={true}
            isActive={true}
          />
        </div>
      )}
      <div className={polymer === 'PVC' && isTinyButtonLayout ? 'mb-3' : ''}>
        {(isOverviewPage && polymer === 'PVC' && isPVCSeriesNavigation) ? (
          // PVC overview 750px-1140px: 2 buttons with chevrons
          <div className="flex justify-center items-center gap-0 mb-3">
            <button
              onClick={() => setSeriesNavigationIndex(Math.max(0, seriesNavigationIndex - 1))}
              disabled={seriesNavigationIndex === 0}
              className={`p-1 rounded transition-colors disabled:opacity-40 ${isDark ? 'text-rs-green hover:bg-rs-jet' : 'text-black hover:bg-rs-off-black/20'}`}
              aria-label="Previous series"
            >
              <ChevronLeft size={28} strokeWidth={2.5} />
            </button>

            <div className="flex gap-1.5">
              {seriesNavigationIndex === 0 ? (
                <>
                  <button
                    onClick={() => toggleSeries('production')}
                    className={`px-3 py-1.5 rounded text-sm font-medium transition-colors hover:brightness-80 bg-rs-green text-black ${
                      visibleSeries.production ? 'opacity-100' : 'opacity-40'
                    }`}
                  >
                    Production
                  </button>
                  <button
                    onClick={() => toggleSeries('demand')}
                    className={`px-3 py-1.5 rounded text-sm font-medium transition-colors brightness-125 hover:brightness-100 ${
                      visibleSeries.demand ? 'opacity-100' : 'opacity-40'
                    }`}
                    style={{
                      backgroundColor: demandColor,
                      color: 'black',
                    }}
                  >
                    Demand / Sales
                  </button>
                </>
              ) : (
                <>
                  {exportsTotal && (
                    <button
                      onClick={() => toggleSeries('exportsTotal')}
                      className={`px-3 py-1.5 rounded text-sm font-medium transition-colors brightness-125 hover:brightness-100 ${
                        visibleSeries.exportsTotal ? 'opacity-100' : 'opacity-40'
                      }`}
                      style={{
                        backgroundColor: exportsColor,
                        color: 'black',
                      }}
                    >
                      Exports Total
                    </button>
                  )}
                  <button
                    onClick={() => toggleSeries('capacity')}
                    className={`px-3 py-1.5 rounded text-sm font-medium transition-colors brightness-125 hover:brightness-100 ${
                      visibleSeries.capacity ? 'opacity-100' : 'opacity-40'
                    }`}
                    style={{
                      backgroundColor: chartColors.warning,
                      color: 'black',
                    }}
                  >
                    Capacity
                  </button>
                </>
              )}
            </div>

            <button
              onClick={() => setSeriesNavigationIndex(Math.min(1, seriesNavigationIndex + 1))}
              disabled={seriesNavigationIndex >= 1}
              className={`p-1 rounded transition-colors disabled:opacity-40 ${isDark ? 'text-rs-green hover:bg-rs-jet' : 'text-black hover:bg-rs-off-black/20'}`}
              aria-label="Next series"
            >
              <ChevronRight size={28} strokeWidth={2.5} />
            </button>
          </div>
        ) : (
          // Normal layout: all buttons
          <div className={`${polymer === 'PVC' && isTinyButtonLayout ? 'grid grid-cols-2 gap-2 w-fit mx-auto mb-3' : `flex gap-1.5 flex-wrap ${(isOverviewPage && polymer === 'PE' && isLargeResinSelector) || isSmallCenterButtons ? 'justify-center' : ''} mb-3`}`}>
          <button
            onClick={() => toggleSeries('production')}
            className={`px-3 py-1.5 rounded text-sm font-medium transition-colors hover:brightness-80 bg-rs-green text-black ${
              visibleSeries.production ? 'opacity-100' : 'opacity-40'
            }`}
          >
            Production
          </button>

          <button
            onClick={() => toggleSeries('demand')}
            className={`px-3 py-1.5 rounded text-sm font-medium transition-colors brightness-125 hover:brightness-100 ${
              visibleSeries.demand ? 'opacity-100' : 'opacity-40'
            }`}
            style={{
              backgroundColor: demandColor,
              color: 'black',
            }}
          >
            Demand / Sales
          </button>

          {polymer === 'PVC' && exportsTotal && (
            <button
              onClick={() => toggleSeries('exportsTotal')}
              className={`px-3 py-1.5 rounded text-sm font-medium transition-colors brightness-125 hover:brightness-100 ${
                visibleSeries.exportsTotal ? 'opacity-100' : 'opacity-40'
              }`}
              style={{
                backgroundColor: exportsColor,
                color: 'black',
              }}
            >
              Exports Total
            </button>
          )}

          <button
            onClick={() => toggleSeries('capacity')}
            className={`px-3 py-1.5 rounded text-sm font-medium transition-colors brightness-125 hover:brightness-100 ${
              visibleSeries.capacity ? 'opacity-100' : 'opacity-40'
            }`}
            style={{
              backgroundColor: chartColors.warning,
              color: 'black',
            }}
          >
            Capacity
          </button>

            {selectedResinType && onResinTypeChange && isXLargeResinSelector && (
              <div className="ml-auto">
                <CheckboxDropdown
                  label="PE Type"
                  options={['ALL PE', 'HDPE', 'LDPE', 'LLDPE']}
                  selected={new Set([selectedResinType === 'pe' ? 'ALL PE' : selectedResinType.toUpperCase()])}
                  onChange={(selected) => {
                    const value = Array.from(selected)[0];
                    const type = value === 'ALL PE' ? 'pe' : value.toLowerCase() as 'pe' | 'hdpe' | 'ldpe' | 'lldpe';
                    onResinTypeChange(type);
                  }}
                  singleSelect={true}
                  hideCount={true}
                  isActive={true}
                />
              </div>
            )}
          </div>
        )}
      </div>

      {/* CHART CONTAINER */}
      <FreeTrialFade enabled={isOverviewPage} className="flex-1 min-h-0 flex flex-col">
        <div className={`flex-1 min-h-0 flex flex-col ${showBorder ? `rounded-lg p-4 ${isDark ? 'bg-rs-off-black/40 border border-rs-jet-light' : 'bg-transparent'}` : `rounded ${isDark ? 'bg-rs-jet' : 'bg-rs-off-black'}`}`}>
          <div className="flex-1 min-h-0">
            <Chart type="bar" data={chartData} options={options} plugins={[hoverLinePlugin]} />
          </div>
          {hasWindowing && (
            <div className="mb-3">
              <ChartRangeSlider
                totalPoints={labels.length}
                startIndex={startIndex}
                endIndex={endIndex}
                onChange={setWindow}
              />
            </div>
          )}
        </div>
      </FreeTrialFade>
      <p data-source-tag className="mt-3 text-rs-highlight-gray text-xs">
        Source: American Chemistry Council® (ACC)
      </p>
    </div>
  );
};


export default DemandProductionCapacityChart;

